import React, { useState } from 'react'
import {
  Hammer,
  Zap,
  Droplets,
  PaintBucket,
  Home,
  Grid,
  ShieldCheck,
  CheckCircle2,
  ArrowRight,
  Search,
  Building2,
  Sparkles,
  Camera,
  Layers,
  ChevronRight,
  PlusCircle,
  Wrench,
  Trees,
  Truck,
  Sparkle,
  Flame,
  Check,
  Lock,
  Sun,
  BatteryCharging,
  Wind,
  Key,
  Compass,
  Ruler,
  ClipboardCheck,
  Bug,
  Plug,
  LayoutGrid,
  ListFilter,
  ArrowLeft,
  X,
  Loader2,
  Clock,
  Trash2,
} from 'lucide-react'
import {
  CATEGORY_SUPER_GROUPS,
  TRADE_CATEGORIES,
  getCategoryInfo,
  type TradeCategory,
  type CategorySuperGroup,
  type Inquiry,
  type CompanyProfile,
} from '../types/poptavky'
import {
  loadSavedInquiryDraft,
  clearSavedInquiryDraft,
  type SavedInquiryDraftState,
} from '../utils/inquiryDraftStorage'

export interface LandingPageProps {
  inquiries: Inquiry[]
  companies: CompanyProfile[]
  onSelectCategoryAndNewInquiry: (category: TradeCategory) => void
  onSelectCategoriesAndNewInquiry?: (categories: TradeCategory[]) => void
  onGenerateAiDraft?: (promptText: string) => Promise<void>
  onResumeSavedDraft?: () => void
  onNavigate: (path: string) => void
  onInspectCompany?: (company: CompanyProfile) => void
}

// Pomocník pro plynulý View Transition v prohlížeči
function runSmoothTransition(callback: () => void) {
  if (typeof document !== 'undefined' && 'startViewTransition' in document) {
    ;(document as any).startViewTransition(() => {
      callback()
    })
  } else {
    callback()
  }
}

// Barevná schémata a přechody pro 9 hlavních oblastí
interface SuperGroupTheme {
  gradientBg: string
  borderHover: string
  iconBg: string
  badgeBg: string
  accentText: string
  bannerGradient: string
  icon: React.ReactNode
}

function getSuperGroupTheme(groupId: CategorySuperGroup): SuperGroupTheme {
  switch (groupId) {
    case 'electrical_group':
      return {
        gradientBg:
          'bg-gradient-to-br from-amber-50/90 via-white to-yellow-50/60 dark:from-amber-950/30 dark:via-slate-900 dark:to-slate-900',
        borderHover: 'hover:border-amber-400 dark:hover:border-amber-500/70',
        iconBg:
          'bg-gradient-to-br from-amber-500 to-orange-500 text-white shadow-md shadow-amber-500/25',
        badgeBg:
          'bg-amber-100/90 dark:bg-amber-950/80 text-amber-800 dark:text-amber-300 border-amber-200/80 dark:border-amber-800/60',
        accentText: 'text-amber-600 dark:text-amber-400',
        bannerGradient:
          'from-amber-500/15 via-orange-500/5 to-transparent dark:from-amber-500/20 dark:via-slate-900 dark:to-slate-900 border-amber-300/70 dark:border-amber-700/60',
        icon: <Zap className="w-6 h-6" />,
      }
    case 'plumbing_heating_group':
      return {
        gradientBg:
          'bg-gradient-to-br from-sky-50/90 via-white to-blue-50/60 dark:from-sky-950/30 dark:via-slate-900 dark:to-slate-900',
        borderHover: 'hover:border-sky-400 dark:hover:border-sky-500/70',
        iconBg:
          'bg-gradient-to-br from-sky-500 to-blue-600 text-white shadow-md shadow-sky-500/25',
        badgeBg:
          'bg-sky-100/90 dark:bg-sky-950/80 text-sky-800 dark:text-sky-300 border-sky-200/80 dark:border-sky-800/60',
        accentText: 'text-sky-600 dark:text-sky-400',
        bannerGradient:
          'from-sky-500/15 via-blue-500/5 to-transparent dark:from-sky-500/20 dark:via-slate-900 dark:to-slate-900 border-sky-300/70 dark:border-sky-700/60',
        icon: <Droplets className="w-6 h-6" />,
      }
    case 'construction':
      return {
        gradientBg:
          'bg-gradient-to-br from-stone-100/90 via-white to-orange-50/40 dark:from-stone-900/60 dark:via-slate-900 dark:to-slate-900',
        borderHover: 'hover:border-stone-400 dark:hover:border-stone-500/70',
        iconBg:
          'bg-gradient-to-br from-stone-600 to-amber-700 text-white shadow-md shadow-stone-600/25',
        badgeBg:
          'bg-stone-200/80 dark:bg-stone-800 text-stone-800 dark:text-stone-200 border-stone-300 dark:border-stone-700',
        accentText: 'text-stone-700 dark:text-stone-300',
        bannerGradient:
          'from-stone-500/15 via-amber-500/5 to-transparent dark:from-stone-700/30 dark:via-slate-900 dark:to-slate-900 border-stone-300 dark:border-stone-700',
        icon: <Building2 className="w-6 h-6" />,
      }
    case 'interior_group':
      return {
        gradientBg:
          'bg-gradient-to-br from-indigo-50/90 via-white to-violet-50/60 dark:from-indigo-950/35 dark:via-slate-900 dark:to-slate-900',
        borderHover: 'hover:border-indigo-400 dark:hover:border-indigo-500/70',
        iconBg:
          'bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-md shadow-indigo-500/25',
        badgeBg:
          'bg-indigo-100/90 dark:bg-indigo-950/80 text-indigo-800 dark:text-indigo-300 border-indigo-200/80 dark:border-indigo-800/60',
        accentText: 'text-indigo-600 dark:text-indigo-400',
        bannerGradient:
          'from-indigo-500/15 via-violet-500/5 to-transparent dark:from-indigo-500/20 dark:via-slate-900 dark:to-slate-900 border-indigo-300/70 dark:border-indigo-700/60',
        icon: <Home className="w-6 h-6" />,
      }
    case 'roof_windows_group':
      return {
        gradientBg:
          'bg-gradient-to-br from-purple-50/90 via-white to-fuchsia-50/50 dark:from-purple-950/35 dark:via-slate-900 dark:to-slate-900',
        borderHover: 'hover:border-purple-400 dark:hover:border-purple-500/70',
        iconBg:
          'bg-gradient-to-br from-purple-600 to-indigo-600 text-white shadow-md shadow-purple-500/25',
        badgeBg:
          'bg-purple-100/90 dark:bg-purple-950/80 text-purple-800 dark:text-purple-300 border-purple-200/80 dark:border-purple-800/60',
        accentText: 'text-purple-600 dark:text-purple-400',
        bannerGradient:
          'from-purple-500/15 via-fuchsia-500/5 to-transparent dark:from-purple-500/20 dark:via-slate-900 dark:to-slate-900 border-purple-300/70 dark:border-purple-700/60',
        icon: <Hammer className="w-6 h-6" />,
      }
    case 'finishing':
      return {
        gradientBg:
          'bg-gradient-to-br from-rose-50/90 via-white to-pink-50/60 dark:from-rose-950/30 dark:via-slate-900 dark:to-slate-900',
        borderHover: 'hover:border-rose-400 dark:hover:border-rose-500/70',
        iconBg:
          'bg-gradient-to-br from-rose-500 to-pink-600 text-white shadow-md shadow-rose-500/25',
        badgeBg:
          'bg-rose-100/90 dark:bg-rose-950/80 text-rose-800 dark:text-rose-300 border-rose-200/80 dark:border-rose-800/60',
        accentText: 'text-rose-600 dark:text-rose-400',
        bannerGradient:
          'from-rose-500/15 via-pink-500/5 to-transparent dark:from-rose-500/20 dark:via-slate-900 dark:to-slate-900 border-rose-300/70 dark:border-rose-700/60',
        icon: <PaintBucket className="w-6 h-6" />,
      }
    case 'garden':
      return {
        gradientBg:
          'bg-gradient-to-br from-emerald-50/90 via-white to-teal-50/60 dark:from-emerald-950/35 dark:via-slate-900 dark:to-slate-900',
        borderHover: 'hover:border-emerald-400 dark:hover:border-emerald-500/70',
        iconBg:
          'bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-md shadow-emerald-500/25',
        badgeBg:
          'bg-emerald-100/90 dark:bg-emerald-950/80 text-emerald-800 dark:text-emerald-300 border-emerald-200/80 dark:border-emerald-800/60',
        accentText: 'text-emerald-600 dark:text-emerald-400',
        bannerGradient:
          'from-emerald-500/15 via-teal-500/5 to-transparent dark:from-emerald-500/20 dark:via-slate-900 dark:to-slate-900 border-emerald-300/70 dark:border-emerald-700/60',
        icon: <Trees className="w-6 h-6" />,
      }
    case 'moving_cleaning_group':
      return {
        gradientBg:
          'bg-gradient-to-br from-orange-50/90 via-white to-amber-50/50 dark:from-orange-950/30 dark:via-slate-900 dark:to-slate-900',
        borderHover: 'hover:border-orange-400 dark:hover:border-orange-500/70',
        iconBg:
          'bg-gradient-to-br from-orange-500 to-red-500 text-white shadow-md shadow-orange-500/25',
        badgeBg:
          'bg-orange-100/90 dark:bg-orange-950/80 text-orange-800 dark:text-orange-300 border-orange-200/80 dark:border-orange-800/60',
        accentText: 'text-orange-600 dark:text-orange-400',
        bannerGradient:
          'from-orange-500/15 via-amber-500/5 to-transparent dark:from-orange-500/20 dark:via-slate-900 dark:to-slate-900 border-orange-300/70 dark:border-orange-700/60',
        icon: <Truck className="w-6 h-6" />,
      }
    case 'projects_special_group':
    default:
      return {
        gradientBg:
          'bg-gradient-to-br from-teal-50/90 via-white to-cyan-50/60 dark:from-teal-950/35 dark:via-slate-900 dark:to-slate-900',
        borderHover: 'hover:border-teal-400 dark:hover:border-teal-500/70',
        iconBg:
          'bg-gradient-to-br from-teal-500 to-cyan-600 text-white shadow-md shadow-teal-500/25',
        badgeBg:
          'bg-teal-100/90 dark:bg-teal-950/80 text-teal-800 dark:text-teal-300 border-teal-200/80 dark:border-teal-800/60',
        accentText: 'text-teal-600 dark:text-teal-400',
        bannerGradient:
          'from-teal-500/15 via-cyan-500/5 to-transparent dark:from-teal-500/20 dark:via-slate-900 dark:to-slate-900 border-teal-300/70 dark:border-teal-700/60',
        icon: <Compass className="w-6 h-6" />,
      }
  }
}

function getCategoryIcon(id: TradeCategory) {
  switch (id) {
    // 1. Elektro
    case 'electrical':
      return <Zap className="w-5 h-5 text-amber-500" />
    case 'elec_switchboard':
      return <Zap className="w-5 h-5 text-yellow-500" />
    case 'elec_lighting_sockets':
      return <Sun className="w-5 h-5 text-amber-400" />
    case 'elec_appliances':
      return <Plug className="w-5 h-5 text-orange-500" />
    case 'elec_revisions_lps':
      return <ShieldCheck className="w-5 h-5 text-emerald-500" />
    case 'elec_solar_wallbox':
      return <BatteryCharging className="w-5 h-5 text-emerald-600" />
    case 'elec_smart_security':
      return <ShieldCheck className="w-5 h-5 text-blue-500" />
    case 'elec_emergency':
      return <Zap className="w-5 h-5 text-rose-500" />

    // 2. Voda, Topení, Plyn & Klima
    case 'plumbing':
      return <Droplets className="w-5 h-5 text-blue-500" />
    case 'plumbing_sanitary':
      return <Droplets className="w-5 h-5 text-cyan-500" />
    case 'plumbing_boilers':
      return <Flame className="w-5 h-5 text-orange-500" />
    case 'plumbing_drains':
      return <Wrench className="w-5 h-5 text-indigo-500" />
    case 'heating':
      return <Flame className="w-5 h-5 text-rose-500" />
    case 'heating_boilers_pumps':
      return <Flame className="w-5 h-5 text-red-500" />
    case 'gas_hvac':
      return <Flame className="w-5 h-5 text-amber-600" />
    case 'aircon_ventilation':
      return <Wind className="w-5 h-5 text-sky-500" />

    // 3. Stavba, Zedníci & Fasády
    case 'rough_construction':
      return <Building2 className="w-5 h-5 text-stone-600" />
    case 'masonry':
      return <Layers className="w-5 h-5 text-stone-500" />
    case 'drywall_attic':
      return <Layers className="w-5 h-5 text-slate-500" />
    case 'concrete_leveling':
      return <Layers className="w-5 h-5 text-zinc-500" />
    case 'facades_insulation':
      return <Home className="w-5 h-5 text-amber-600" />
    case 'waterproofing_damp':
      return <ShieldCheck className="w-5 h-5 text-cyan-600" />
    case 'demolition_core_drilling':
      return <Hammer className="w-5 h-5 text-red-600" />

    // 4. Kuchyně, Koupelny & Interiér
    case 'kitchen':
      return <Home className="w-5 h-5 text-amber-600" />
    case 'kitchen_assembly':
      return <Hammer className="w-5 h-5 text-amber-500" />
    case 'bathroom':
      return <Droplets className="w-5 h-5 text-cyan-500" />
    case 'tiling':
      return <Grid className="w-5 h-5 text-teal-600" />
    case 'flooring':
      return <Grid className="w-5 h-5 text-emerald-600" />
    case 'parquet_renovation':
      return <Grid className="w-5 h-5 text-amber-700" />
    case 'carpentry':
      return <Hammer className="w-5 h-5 text-amber-800" />
    case 'reconstruction':
      return <Building2 className="w-5 h-5 text-indigo-600" />

    // 5. Střechy, Okna, Kominíci & Kovy
    case 'roofing':
      return <Home className="w-5 h-5 text-purple-600" />
    case 'carpentry_roof_pergola':
      return <Hammer className="w-5 h-5 text-amber-700" />
    case 'tinsmith_gutters':
      return <Wrench className="w-5 h-5 text-slate-600" />
    case 'chimney_sweeps':
      return <Flame className="w-5 h-5 text-stone-700" />
    case 'windows_doors':
      return <Grid className="w-5 h-5 text-teal-500" />
    case 'blinds_shading':
      return <Grid className="w-5 h-5 text-sky-600" />
    case 'locksmith_metalwork':
      return <Lock className="w-5 h-5 text-slate-700" />

    // 6. Malování, Montáže & Opravy
    case 'painting':
      return <PaintBucket className="w-5 h-5 text-rose-500" />
    case 'coating_lacquering':
      return <PaintBucket className="w-5 h-5 text-pink-600" />
    case 'wallpapering_stucco':
      return <Layers className="w-5 h-5 text-violet-500" />
    case 'furniture_assembly':
      return <Hammer className="w-5 h-5 text-sky-600" />
    case 'drilling_hanging':
      return <Hammer className="w-5 h-5 text-blue-600" />
    case 'handyman':
      return <Wrench className="w-5 h-5 text-amber-500" />
    case 'locks_security_doors':
      return <Key className="w-5 h-5 text-slate-600" />

    // 7. Zahrada, Stromy, Ploty & Výkopy
    case 'garden_mowing':
      return <Trees className="w-5 h-5 text-emerald-500" />
    case 'garden_hedges_weeding':
      return <Trees className="w-5 h-5 text-green-600" />
    case 'garden_planting_design':
      return <Trees className="w-5 h-5 text-teal-500" />
    case 'wood_chopping':
      return <Hammer className="w-5 h-5 text-amber-700" />
    case 'tree_felling_expert':
      return <Trees className="w-5 h-5 text-emerald-700" />
    case 'stump_grinding_chipping':
      return <Trees className="w-5 h-5 text-stone-600" />
    case 'fencing_gates':
      return <Grid className="w-5 h-5 text-stone-700" />
    case 'fencing_paving':
      return <Grid className="w-5 h-5 text-stone-500" />
    case 'excavation_wells':
      return <Wrench className="w-5 h-5 text-orange-600" />

    // 8. Stěhování, Vyklízení & Úklid
    case 'moving_apartments':
      return <Truck className="w-5 h-5 text-orange-500" />
    case 'moving_help':
      return <Truck className="w-5 h-5 text-amber-600" />
    case 'clearing_cellars':
      return <Truck className="w-5 h-5 text-stone-600" />
    case 'demolition_digging':
      return <Hammer className="w-5 h-5 text-rose-600" />
    case 'home_cleaning':
      return <Sparkle className="w-5 h-5 text-pink-500" />
    case 'window_upholstery_cleaning':
      return <Sparkle className="w-5 h-5 text-sky-500" />
    case 'post_renovation_cleaning':
      return <Sparkle className="w-5 h-5 text-purple-500" />

    // 9. Projekty, Dozor & Speciální
    case 'architect_interior':
      return <Compass className="w-5 h-5 text-indigo-500" />
    case 'project_engineer':
      return <Ruler className="w-5 h-5 text-blue-600" />
    case 'building_supervisor':
      return <ClipboardCheck className="w-5 h-5 text-emerald-600" />
    case 'geodesist':
      return <Compass className="w-5 h-5 text-amber-600" />
    case 'pest_control':
      return <Bug className="w-5 h-5 text-red-500" />
    case 'other':
    default:
      return <Sparkles className="w-5 h-5 text-brand-500" />
  }
}

export const LandingPage: React.FC<LandingPageProps> = ({
  inquiries: _inquiries,
  companies: _companies,
  onSelectCategoryAndNewInquiry,
  onSelectCategoriesAndNewInquiry,
  onGenerateAiDraft,
  onResumeSavedDraft,
  onNavigate,
  onInspectCompany: _onInspectCompany,
}) => {
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [isGeneratingDraft, setIsGeneratingDraft] = useState<boolean>(false)
  const [draftError, setDraftError] = useState<string | null>(null)
  const [savedDraft, setSavedDraft] = useState<SavedInquiryDraftState | null>(() =>
    loadSavedInquiryDraft()
  )
  // Výchozí stav je přehledných 9 hlavních oblastí ('overview'), ne obří zeď všech 67 karet
  const [selectedGroupFilter, setSelectedGroupFilter] = useState<
    CategorySuperGroup | 'overview' | 'all'
  >('overview')
  // Možnost naklikat si rovnou na hlavní stránce více oborů najednou (např. Elektro + Voda)
  const [selectedBasketCategories, setSelectedBasketCategories] = useState<TradeCategory[]>([])

  const handleSmartTemplateSubmit = async (customText?: string) => {
    const textToUse = (customText ?? searchQuery).trim()
    if (!textToUse) {
      handleProceedWithBasket()
      return
    }
    if (!onGenerateAiDraft) {
      handleProceedWithBasket()
      return
    }
    setDraftError(null)
    setIsGeneratingDraft(true)
    try {
      await onGenerateAiDraft(textToUse)
    } catch (err: any) {
      setDraftError(err?.message || 'Nepodařilo se připravit šablonu, zkuste to prosím znovu.')
    } finally {
      setIsGeneratingDraft(false)
    }
  }

  const handleToggleBasketCategory = (catId: TradeCategory, e?: React.MouseEvent) => {
    if (e) e.stopPropagation()
    setSelectedBasketCategories((prev) =>
      prev.includes(catId) ? prev.filter((c) => c !== catId) : [...prev, catId]
    )
  }

  const handleProceedWithBasket = (extraCat?: TradeCategory) => {
    const finalCats = extraCat
      ? Array.from(new Set([...selectedBasketCategories, extraCat]))
      : selectedBasketCategories
    if (finalCats.length === 0) {
      onNavigate('/zadat-poptavku')
    } else if (onSelectCategoriesAndNewInquiry) {
      onSelectCategoriesAndNewInquiry(finalCats)
    } else {
      onSelectCategoryAndNewInquiry(finalCats[0])
    }
  }

  const handleChangeGroup = (nextGroup: CategorySuperGroup | 'overview' | 'all') => {
    runSmoothTransition(() => {
      setSelectedGroupFilter(nextGroup)
    })
  }

  // Filtrování dlaždic řemesel při vyhledávání nebo rozkliknutí oblasti
  const filteredCategories = TRADE_CATEGORIES.filter((cat) => {
    if (
      selectedGroupFilter !== 'overview' &&
      selectedGroupFilter !== 'all' &&
      cat.group !== selectedGroupFilter
    ) {
      return false
    }
    if (!searchQuery.trim()) return true
    const q = searchQuery.toLowerCase()
    return (
      cat.label.toLowerCase().includes(q) ||
      cat.shortDesc.toLowerCase().includes(q) ||
      cat.id.toLowerCase().includes(q) ||
      (cat.subTasks && cat.subTasks.some((sub) => sub.toLowerCase().includes(q)))
    )
  })

  // Karta jednotlivého řemesla / kategorie (s možností přidat více oborů najednou)
  const renderCategoryCard = (cat: (typeof TRADE_CATEGORIES)[number]) => {
    const groupTheme = getSuperGroupTheme(cat.group)
    const isInBasket = selectedBasketCategories.includes(cat.id)
    return (
      <div
        key={cat.id}
        onClick={() => {
          if (selectedBasketCategories.length > 0) {
            handleToggleBasketCategory(cat.id)
          } else {
            onSelectCategoryAndNewInquiry(cat.id)
          }
        }}
        className={`group relative h-full flex flex-col justify-between text-left p-4 sm:p-5 rounded-2xl border transition-card cursor-pointer ${
          isInBasket
            ? 'bg-brand-50/90 dark:bg-brand-950/50 border-brand-500 ring-2 ring-brand-500/25 shadow-md'
            : `bg-white dark:bg-slate-800/90 border-slate-200/90 dark:border-slate-700/80 ${groupTheme.borderHover}`
        }`}
      >
        <div>
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-2xl bg-slate-50 dark:bg-slate-700/90 border border-slate-200/70 dark:border-slate-600 flex items-center justify-center group-hover:scale-110 shadow-2xs transition-transform duration-200 shrink-0">
              {getCategoryIcon(cat.id)}
            </div>
            <button
              type="button"
              onClick={(e) => handleToggleBasketCategory(cat.id, e)}
              title="Přidat obor do kombinované poptávky (např. Elektro + Voda)"
              className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-[11px] font-extrabold border transition-all cursor-pointer whitespace-nowrap shrink-0 ${
                isInBasket
                  ? 'bg-brand-600 text-white border-brand-600 shadow-xs'
                  : 'bg-slate-100/90 dark:bg-slate-700/80 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-600 hover:border-brand-400 hover:text-brand-600 dark:hover:text-brand-300'
              }`}
            >
              {isInBasket ? (
                <>
                  <Check className="w-3.5 h-3.5" />
                  <span>Vybráno</span>
                </>
              ) : (
                <>
                  <PlusCircle className="w-3.5 h-3.5" />
                  <span>+ Vybrat více</span>
                </>
              )}
            </button>
          </div>

          <div className="flex items-center gap-1.5 mb-2">
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold border whitespace-nowrap ${
                cat.providerTarget === 'company_only'
                  ? 'bg-amber-50 dark:bg-amber-950/70 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800'
                  : 'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
              }`}
            >
              {cat.providerTarget === 'company_only' ? 'Pouze firmy s IČO' : 'Firmy i brigádníci'}
            </span>
          </div>

          <h3 className="font-extrabold text-sm sm:text-base text-slate-900 dark:text-white mb-1.5 group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
            {cat.label}
          </h3>

          <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 leading-relaxed mb-3">
            {cat.shortDesc}
          </p>

          {/* Štítky prvních 3 konkrétních podčinností oboru */}
          {cat.subTasks && cat.subTasks.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3.5">
              {cat.subTasks.slice(0, 3).map((st, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center text-[10px] sm:text-[11px] px-2 py-0.5 rounded-lg bg-slate-50 dark:bg-slate-700/70 text-slate-600 dark:text-slate-300 border border-slate-200/80 dark:border-slate-600/80 truncate max-w-full"
                  title={st}
                >
                  {st}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="mt-auto pt-3 border-t border-slate-100 dark:border-slate-700/70 flex items-center justify-between gap-1.5 min-w-0 text-[11px]">
          <span className="text-slate-400 dark:text-slate-500 font-medium truncate">
            {cat.subTasks ? `${cat.subTasks.length} konkrétních prací` : 'Vybrat obor'}
          </span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              handleProceedWithBasket(cat.id)
            }}
            className="font-extrabold text-brand-600 dark:text-brand-400 group-hover:translate-x-0.5 transition-transform shrink-0 ml-auto cursor-pointer"
          >
            {selectedBasketCategories.length > 0 && !isInBasket
              ? '+ Přidat a poptat →'
              : 'Poptat →'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div
      className={`w-full space-y-10 sm:space-y-14 ${
        selectedBasketCategories.length > 0 ? 'pb-28 sm:pb-32' : 'pb-14'
      } animate-fade-up`}
    >
      {/* ─── 1. HERO SEKCE S JEMNÝM BAREVNÝM PŘECHODEM A RYCHLÝM VYHLEDÁVÁNÍM ─── */}
      <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-50 via-white to-indigo-50/90 dark:from-slate-900 dark:via-slate-900 dark:to-indigo-950/70 text-slate-900 dark:text-white p-4 sm:p-8 md:p-14 border border-slate-200/90 dark:border-slate-800 shadow-xl">
        {/* Animované světelné přechody na pozadí */}
        <div className="absolute -top-28 -right-24 w-96 h-96 bg-gradient-to-br from-brand-400/20 to-indigo-500/20 dark:from-brand-500/25 dark:to-indigo-500/20 rounded-full blur-3xl pointer-events-none animate-glow-slow" />
        <div className="absolute -bottom-28 -left-24 w-96 h-96 bg-gradient-to-tr from-sky-400/15 to-emerald-400/15 dark:from-sky-500/15 dark:to-emerald-500/15 rounded-full blur-3xl pointer-events-none animate-glow-slow" />

        <div className="relative z-10 max-w-3xl mx-auto text-center space-y-5 sm:space-y-6">
          <div className="inline-flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-3.5 py-1.5 rounded-full bg-white/90 dark:bg-slate-800/90 backdrop-blur-md text-[11px] sm:text-sm font-bold text-brand-700 dark:text-brand-300 border border-brand-200/80 dark:border-slate-700 shadow-xs max-w-full">
            <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-brand-600 dark:text-brand-400 shrink-0" />
            <span>Poptávkový portál pro ČR • Kombinace více řemesel</span>
          </div>

          <h1 className="text-2xl sm:text-4xl md:text-5xl font-black tracking-tight leading-tight">
            Zadejte poptávku a získejte{' '}
            <span className="bg-gradient-to-r from-brand-600 via-indigo-600 to-sky-500 dark:from-brand-400 dark:via-indigo-400 dark:to-sky-400 bg-clip-text text-transparent">
              cenové nabídky
            </span>{' '}
            z vašeho okolí
          </h1>

          <p className="text-sm sm:text-base md:text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Vyberte si jeden nebo klidně více oborů najednou (např. elektro + voda + zedník),
            zaškrtněte co přesně potřebujete a volitelně přiložte fotky či vlastní nákres místnosti.
          </p>

          {/* Chytré zadání: Napište co potřebujete a automaticky připravíme vyplněnou šablonu */}
          <div className="max-w-2xl mx-auto pt-2 relative space-y-3">
            <form
              onSubmit={(e) => {
                e.preventDefault()
                handleSmartTemplateSubmit()
              }}
              aria-label="Vytvořit poptávku pomocí umělé inteligence"
              {...({
                toolname: 'create_ai_inquiry_draft',
                tooldescription:
                  'Vytvoří předvyplněnou šablonu poptávky řemeslníků a stavebních firem na NaceniTo.cz podle textového zadání uživatele.',
              } as any)}
              className="relative flex flex-col sm:flex-row items-stretch sm:items-center gap-2 bg-white dark:bg-slate-800/95 p-2 rounded-2xl border-2 border-slate-200/90 dark:border-slate-700 shadow-lg shadow-brand-900/5 focus-within:border-brand-500 focus-within:ring-4 focus-within:ring-brand-500/15 transition-all"
            >
              <div className="relative flex-1 flex items-center min-w-0">
                <Sparkles className="w-5 h-5 text-brand-600 dark:text-brand-400 ml-3 shrink-0 pointer-events-none" />
                <input
                  type="text"
                  name="prompt"
                  aria-label="Popište, co potřebujete udělat nebo nacenit"
                  {...({
                    toolparamdescription:
                      'Jednou větou popište požadovanou práci, rekonstrukci nebo řemeslo (např. rekonstrukce koupelny v Brně).',
                  } as any)}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value)
                    if (draftError) setDraftError(null)
                  }}
                  disabled={isGeneratingDraft}
                  placeholder="Co potřebujete udělat? (např. potřebuji rekonstrukci kuchyně, posunout zásuvky a zapojit myčku...)"
                  className="w-full pl-3 pr-9 py-2.5 bg-transparent text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 text-sm sm:text-base focus:outline-none border-none shadow-none"
                />
                {searchQuery && !isGeneratingDraft && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery('')
                      setDraftError(null)
                    }}
                    title="Vymazat zadání"
                    aria-label="Vymazat zadání"
                    className="absolute right-2 p-2 rounded-lg text-slate-500 hover:text-slate-800 dark:text-slate-300 dark:hover:text-white cursor-pointer"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              <button
                type="submit"
                disabled={isGeneratingDraft}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 min-h-[44px] rounded-xl bg-gradient-to-r from-brand-600 to-indigo-600 hover:from-brand-500 hover:to-indigo-500 active:scale-[0.98] disabled:opacity-70 text-white font-extrabold text-xs sm:text-sm shadow-md shadow-brand-600/20 cursor-pointer transition-all shrink-0"
              >
                {isGeneratingDraft ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>AI připravuje šablonu...</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    <span>
                      {searchQuery.trim()
                        ? 'Vytvořit vyplněnou poptávku'
                        : 'Zadat poptávku'}
                    </span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            {/* Moderní animovaný AI Loading Skeleton při generování šablony */}
            {isGeneratingDraft && (
              <div
                role="status"
                aria-live="polite"
                className="p-4 sm:p-5 rounded-2xl bg-white/95 dark:bg-slate-900/95 border-2 border-brand-500/50 shadow-xl text-left space-y-3 animate-scale-in"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-600 to-indigo-600 text-white flex items-center justify-center shrink-0 shadow-sm">
                    <Loader2 className="w-5 h-5 animate-spin" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs sm:text-sm font-extrabold text-slate-900 dark:text-white">
                      Umělá inteligence sestavuje vaši šablonu poptávky...
                    </div>
                    <div className="text-[11px] text-brand-700 dark:text-brand-300 font-semibold">
                      Analyzuji potřebná řemesla • Vyplňuji podčinnosti • Odhaduji rozsah a rozpočet
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 pt-1">
                  <div className="h-2.5 rounded-full bg-brand-200 dark:bg-brand-900/80 shimmer-box" />
                  <div className="h-2.5 rounded-full bg-indigo-200 dark:bg-indigo-900/80 shimmer-box" />
                  <div className="h-2.5 rounded-full bg-sky-200 dark:bg-sky-900/80 shimmer-box" />
                </div>
              </div>
            )}

            {draftError && (
              <div className="text-xs font-bold text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/50 border border-rose-200 dark:border-rose-800 px-3.5 py-2 rounded-xl animate-scale-in">
                {draftError}
              </div>
            )}

            {/* Ukázková zadání jednou větou (1 klikem připraví kompletní vyplněnou šablonu) */}
            <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
              <span className="text-xs font-bold text-brand-700 dark:text-brand-300 mr-1 inline-flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5" />
                Vyzkoušejte chytré vyplnění šablony:
              </span>
              {[
                'Potřebuji rekonstrukci kuchyně, novou elektřinu pro indukci a zapojit myčku',
                'Kompletní rekonstrukce koupelny, rozvody vody a nové obklady',
                'Posekat zahradu, ostříhat živý plot a pořezat dřevo',
              ].map((samplePrompt, idx) => (
                <button
                  key={idx}
                  type="button"
                  disabled={isGeneratingDraft}
                  onClick={() => {
                    setSearchQuery(samplePrompt)
                    handleSmartTemplateSubmit(samplePrompt)
                  }}
                  className="px-3 py-1.5 min-h-[36px] rounded-xl bg-brand-50/95 dark:bg-brand-950/60 hover:bg-brand-100 dark:hover:bg-brand-900/80 text-xs font-semibold text-brand-800 dark:text-brand-200 border border-brand-200/90 dark:border-brand-800/70 transition-all cursor-pointer active:scale-[0.98]"
                >
                  „{samplePrompt}“
                </button>
              ))}
            </div>

            {/* Rychlé štítky nejčastějších prací a kombinací více řemesel */}
            <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
              <span className="text-xs font-semibold text-slate-600 dark:text-slate-300 mr-1">
                Rychlá volba oborů:
              </span>
              {[
                { label: '⚡ + 💧 Elektro + Voda', cats: ['electrical', 'plumbing'] as TradeCategory[] },
                {
                  label: '🍳 Kuchyně + Elektro + Voda',
                  cats: ['kitchen', 'electrical', 'plumbing'] as TradeCategory[],
                },
                {
                  label: '🚿 Koupelna + Voda + Obklady',
                  cats: ['bathroom', 'plumbing', 'tiling'] as TradeCategory[],
                },
                { label: '⚡ Elektroinstalace', cats: ['electrical'] as TradeCategory[] },
                { label: '🎨 Malování pokojů', cats: ['painting'] as TradeCategory[] },
                { label: '🌿 Sekání zahrady', cats: ['garden_mowing'] as TradeCategory[] },
              ].map((pill, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => {
                    if (onSelectCategoriesAndNewInquiry) {
                      onSelectCategoriesAndNewInquiry(pill.cats)
                    } else {
                      onSelectCategoryAndNewInquiry(pill.cats[0])
                    }
                  }}
                  className="px-3.5 py-2 min-h-[38px] rounded-xl bg-white/95 dark:bg-slate-800/95 hover:bg-brand-50 dark:hover:bg-slate-700 text-xs font-bold text-slate-800 dark:text-slate-100 hover:text-brand-700 dark:hover:text-brand-300 border border-slate-200/90 dark:border-slate-700 shadow-2xs transition-all cursor-pointer hover:-translate-y-0.5 active:scale-[0.98]"
                >
                  {pill.label}
                </button>
              ))}
            </div>
          </div>

          {/* Hlavní CTA tlačítko */}
          {selectedBasketCategories.length > 0 && (
            <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => handleProceedWithBasket()}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-2xl bg-gradient-to-r from-brand-600 to-indigo-600 hover:from-brand-500 hover:to-indigo-500 text-white font-extrabold text-base sm:text-lg shadow-xl shadow-brand-600/25 transition-all cursor-pointer transform hover:-translate-y-0.5"
              >
                <PlusCircle className="w-5 h-5" />
                Pokračovat s vybranými obory ({selectedBasketCategories.length})
                <ArrowRight className="w-4 h-4 ml-0.5" />
              </button>
            </div>
          )}

          {/* 3 přehledné garance v Hero sekci */}
          <div className="pt-5 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs font-semibold text-slate-700 dark:text-slate-200 border-t border-slate-200/80 dark:border-slate-800">
            <div className="flex items-center justify-center gap-2 bg-white/75 dark:bg-slate-800/60 py-2.5 px-3 rounded-xl border border-slate-200/70 dark:border-slate-800">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
              <span>100% zdarma a nezávazně</span>
            </div>
            <div className="flex items-center justify-center gap-2 bg-white/75 dark:bg-slate-800/60 py-2.5 px-3 rounded-xl border border-slate-200/70 dark:border-slate-800">
              <Layers className="w-4 h-4 text-brand-600 dark:text-brand-400 shrink-0" />
              <span>1 i více řemesel v jednom zadání</span>
            </div>
            <div className="flex items-center justify-center gap-2 bg-white/75 dark:bg-slate-800/60 py-2.5 px-3 rounded-xl border border-slate-200/70 dark:border-slate-800">
              <Lock className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
              <span>Soukromé kontakty až do výběru</span>
            </div>
          </div>
        </div>
      </section>

      {/* ─── 1B. BANNER ROZPRACOVANÉ POPTÁVKY (FUNGUJE I BEZ PŘIHLÁŠENÍ) ─── */}
      {savedDraft && (
        <section
          aria-label="Rozpracovaná poptávka"
          className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-emerald-50/95 via-white to-brand-50/90 dark:from-emerald-950/45 dark:via-slate-900 dark:to-brand-950/40 border-2 border-emerald-400/70 dark:border-emerald-700/70 p-5 sm:p-7 shadow-lg animate-scale-in"
        >
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex items-start gap-3.5 min-w-0 flex-1">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white flex items-center justify-center shrink-0 shadow-md shadow-emerald-500/20">
                <Clock className="w-6 h-6" />
              </div>
              <div className="space-y-1.5 min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-950/90 text-emerald-800 dark:text-emerald-300 border border-emerald-300/80 dark:border-emerald-800 text-[11px] font-extrabold">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    Rozpracovaná poptávka v prohlížeči (Krok {savedDraft.step} ze 3)
                  </span>
                  {savedDraft.locationData?.city && (
                    <span className="text-xs font-bold text-slate-600 dark:text-slate-300">
                      📍 {savedDraft.locationData.city}
                    </span>
                  )}
                </div>

                <h2 className="text-base sm:text-lg font-black text-slate-900 dark:text-white truncate">
                  {savedDraft.title?.trim() ||
                    savedDraft.selectedCategories
                      .map((c) => getCategoryInfo(c).label)
                      .join(' + ') ||
                    'Nedokončené zadání poptávky'}
                </h2>

                {savedDraft.description?.trim() && (
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 line-clamp-2 leading-relaxed">
                    {savedDraft.description}
                  </p>
                )}

                <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                  {savedDraft.selectedCategories.map((catId) => {
                    const info = getCategoryInfo(catId)
                    return (
                      <span
                        key={catId}
                        className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-[11px] font-bold text-slate-800 dark:text-slate-200"
                      >
                        {getCategoryIcon(catId)}
                        <span>{info.label}</span>
                      </span>
                    )
                  })}
                  {savedDraft.attachedFloorplan && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg bg-indigo-50 dark:bg-indigo-950/70 border border-indigo-200 dark:border-indigo-800 text-[11px] font-bold text-indigo-800 dark:text-indigo-200">
                      📐 Přiložen 2D půdorys ({savedDraft.attachedFloorplan.totalAreaM2} m²)
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap sm:flex-nowrap items-center gap-2.5 shrink-0 pt-2 lg:pt-0">
              <button
                type="button"
                onClick={() => {
                  clearSavedInquiryDraft()
                  setSavedDraft(null)
                }}
                aria-label="Smazat rozpracovanou poptávku"
                className="inline-flex items-center justify-center gap-1.5 px-4 py-3 min-h-[44px] rounded-xl bg-white dark:bg-slate-800 hover:bg-rose-50 dark:hover:bg-rose-950/40 text-slate-700 dark:text-slate-200 hover:text-rose-600 border border-slate-200 dark:border-slate-700 text-xs font-bold transition-all cursor-pointer"
              >
                <Trash2 className="w-4 h-4" />
                <span>Zahodit návrh</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  if (onResumeSavedDraft) {
                    onResumeSavedDraft()
                  } else {
                    onNavigate('/zadat-poptavku')
                  }
                }}
                className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 px-6 py-3 min-h-[44px] rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 active:scale-[0.98] text-white font-extrabold text-xs sm:text-sm shadow-md shadow-emerald-600/20 transition-all cursor-pointer"
              >
                <span>Pokračovat v rozpracované poptávce</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </section>
      )}

      {/* ─── 2. INTERAKTIVNÍ ROZCESTNÍK KATEGORIÍ (2 ÚROVNĚ S PLYNULÝMI PŘECHODY) ─── */}
      <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-5 sm:p-8 md:p-10 shadow-sm space-y-7">
        {/* Hlavička katalogu + přepínač zobrazení */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 pb-4 border-b border-slate-200/80 dark:border-slate-800">
          <div className="space-y-1">
            <span className="inline-flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-wider text-brand-600 dark:text-brand-400">
              <LayoutGrid className="w-3.5 h-3.5" />
              Přehledný rozcestník prací • Lze vybrat i více oborů najednou
            </span>
            <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight">
              Co potřebujete vyřešit nebo zrealizovat?
            </h2>
            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
              Klikněte přímo na obor, nebo pomocí tlačítka <strong>+ Přidat obor</strong> nakombinujte více řemesel (např. Elektro + Voda).
            </p>
          </div>

          {/* Přepínač režimu zobrazení: 9 hlavních oblastí vs. Všech 67 kategorií */}
          {!searchQuery.trim() && (
            <div className="inline-flex items-center p-1 rounded-2xl bg-slate-100 dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700 self-start md:self-auto shrink-0">
              <button
                type="button"
                onClick={() => handleChangeGroup('overview')}
                className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-extrabold transition-all cursor-pointer ${
                  selectedGroupFilter === 'overview'
                    ? 'bg-white dark:bg-slate-900 text-brand-600 dark:text-brand-400 shadow-xs'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                <LayoutGrid className="w-3.5 h-3.5" />
                9 hlavních oblastí
              </button>
              <button
                type="button"
                onClick={() => handleChangeGroup('all')}
                className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-extrabold transition-all cursor-pointer ${
                  selectedGroupFilter === 'all'
                    ? 'bg-white dark:bg-slate-900 text-brand-600 dark:text-brand-400 shadow-xs'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                <ListFilter className="w-3.5 h-3.5" />
                Všech {TRADE_CATEGORIES.length} oborů
              </button>
            </div>
          )}
        </div>

        {/* Lišta vybraných oborů, pokud si uživatel skládá více řemesel */}
        {selectedBasketCategories.length > 0 && (
          <div className="p-4 sm:p-5 rounded-2xl bg-gradient-to-r from-brand-50 via-indigo-50/80 to-sky-50/70 dark:from-brand-950/60 dark:via-slate-900 dark:to-indigo-950/50 border-2 border-brand-500/40 shadow-sm space-y-3 animate-scale-in">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <span className="text-[11px] font-extrabold uppercase tracking-wider text-brand-600 dark:text-brand-400">
                  Váš výběr oborů pro poptávku ({selectedBasketCategories.length})
                </span>
                <p className="text-xs text-slate-600 dark:text-slate-300">
                  Můžete přidat další řemesla z libovolné oblasti nebo rovnou pokračovat k zadání.
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setSelectedBasketCategories([])}
                  className="px-3 py-2 rounded-xl text-xs font-bold text-slate-500 hover:text-rose-600 dark:text-slate-400 cursor-pointer"
                >
                  Vymazat vše
                </button>
                <button
                  type="button"
                  onClick={() => handleProceedWithBasket()}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-brand-600 to-indigo-600 hover:from-brand-500 hover:to-indigo-500 text-white text-xs sm:text-sm font-extrabold shadow-md shadow-brand-600/20 cursor-pointer transition-all"
                >
                  <span>Pokračovat s vybranými obory ({selectedBasketCategories.length})</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {selectedBasketCategories.map((catId) => {
                const info = getCategoryInfo(catId)
                return (
                  <span
                    key={catId}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white dark:bg-slate-800 border border-brand-300 dark:border-brand-700 text-xs font-extrabold text-slate-900 dark:text-white shadow-2xs"
                  >
                    {getCategoryIcon(catId)}
                    <span>{info.label}</span>
                    <button
                      type="button"
                      onClick={() => handleToggleBasketCategory(catId)}
                      className="text-slate-400 hover:text-rose-600 cursor-pointer"
                      title="Odebrat obor"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </span>
                )
              })}
            </div>
          </div>
        )}

        {/* Rychlá horizontální lišta 9 oblastí (vždy po ruce pro okamžité přepínání) */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1.5 no-scrollbar">
          <button
            type="button"
            onClick={() => handleChangeGroup('overview')}
            className={`px-3.5 py-2 rounded-xl text-xs font-extrabold whitespace-nowrap transition-all cursor-pointer ${
              selectedGroupFilter === 'overview' && !searchQuery.trim()
                ? 'bg-gradient-to-r from-brand-600 to-indigo-600 text-white shadow-sm'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
            }`}
          >
            ✨ Přehled oblastí (9)
          </button>

          {CATEGORY_SUPER_GROUPS.map((sg) => {
            const count = TRADE_CATEGORIES.filter((c) => c.group === sg.id).length
            const isSel = selectedGroupFilter === sg.id
            return (
              <button
                key={sg.id}
                type="button"
                onClick={() => {
                  setSearchQuery('')
                  handleChangeGroup(sg.id)
                }}
                className={`px-3.5 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all cursor-pointer ${
                  isSel
                    ? 'bg-gradient-to-r from-brand-600 to-indigo-600 text-white shadow-sm scale-[1.02]'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                }`}
              >
                {sg.tabLabel} ({count})
              </button>
            )
          })}
        </div>

        {/* ─── REŽIM A: VYHLEDÁVÁNÍ (pokud uživatel píše do vyhledávače) ─── */}
        {searchQuery.trim() ? (
          <div key="search-results" className="space-y-4 animate-scale-in">
            <div className="flex items-center justify-between bg-brand-50/70 dark:bg-slate-800/70 px-4 py-3 rounded-2xl border border-brand-200/70 dark:border-slate-700">
              <span className="text-xs sm:text-sm font-bold text-slate-800 dark:text-slate-200">
                Výsledky hledání pro „<span className="text-brand-600 dark:text-brand-400">{searchQuery}</span>“ ({filteredCategories.length} oborů)
              </span>
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="text-xs font-extrabold text-brand-600 dark:text-brand-400 hover:underline cursor-pointer"
              >
                Zrušit filtr &times;
              </button>
            </div>

            {filteredCategories.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredCategories.map(renderCategoryCard)}
              </div>
            ) : (
              <div className="text-center py-12 px-4 rounded-2xl bg-slate-50 dark:bg-slate-800/40 border border-dashed border-slate-300 dark:border-slate-700 space-y-3">
                <p className="text-sm font-bold text-slate-700 dark:text-slate-300">
                  Pro hledaný výraz jsme nenašli přesnou škatulku, ale poptávku můžete zadat jako obecnou zakázku.
                </p>
                <button
                  type="button"
                  onClick={() => onSelectCategoryAndNewInquiry('other')}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-600 text-white text-xs font-extrabold shadow-sm hover:bg-brand-500 cursor-pointer"
                >
                  <PlusCircle className="w-4 h-4" />
                  Zadat individuální poptávku
                </button>
              </div>
            )}
          </div>
        ) : selectedGroupFilter === 'overview' ? (
          /* ─── REŽIM B (VÝCHOZÍ): 9 PŘEHLEDNÝCH BENTO KARET HLAVNÍCH OBLASTÍ ─── */
          <div
            key="overview-bento"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 animate-scale-in"
          >
            {CATEGORY_SUPER_GROUPS.map((sg) => {
              const groupCats = TRADE_CATEGORIES.filter((c) => c.group === sg.id)
              const theme = getSuperGroupTheme(sg.id)

              return (
                <div
                  key={sg.id}
                  className={`group flex flex-col justify-between rounded-3xl p-6 border border-slate-200/90 dark:border-slate-800 ${theme.gradientBg} ${theme.borderHover} transition-card`}
                >
                  <div>
                    {/* Horní řádek s ikonou v barevném přechodu a počtem oborů */}
                    <div className="flex items-start justify-between gap-3 mb-4">
                      <button
                        type="button"
                        onClick={() => handleChangeGroup(sg.id)}
                        aria-label={`Zobrazit oblast ${sg.title}`}
                        title={sg.title}
                        className={`w-13 h-13 rounded-2xl flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-105 cursor-pointer ${theme.iconBg}`}
                      >
                        {theme.icon}
                      </button>

                      <button
                        type="button"
                        onClick={() => handleChangeGroup(sg.id)}
                        className={`inline-flex items-center gap-1 px-3 py-1.5 min-h-[34px] rounded-full text-xs font-extrabold border transition-transform group-hover:translate-x-0.5 cursor-pointer ${theme.badgeBg}`}
                      >
                        <span>{groupCats.length} oborů</span>
                        <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    {/* Název a popis oblasti */}
                    <button
                      type="button"
                      onClick={() => handleChangeGroup(sg.id)}
                      className="text-left block w-full group/title cursor-pointer mb-4"
                    >
                      <h3 className="text-lg sm:text-xl font-black text-slate-900 dark:text-white group-hover/title:text-brand-600 dark:group-hover/title:text-brand-400 transition-colors">
                        {sg.title}
                      </h3>
                      <p className="text-xs text-slate-600 dark:text-slate-300 mt-1 line-clamp-2 leading-relaxed">
                        {sg.subtitle}
                      </p>
                    </button>

                    {/* Rychlé prokliky na 4 nejčastější řemesla v dané oblasti přímo z karty */}
                    <div className="space-y-1.5 mb-5">
                      {groupCats.slice(0, 4).map((cat) => {
                        const isPicked = selectedBasketCategories.includes(cat.id)
                        return (
                          <div
                            key={cat.id}
                            className={`w-full flex items-center justify-between gap-1.5 px-2.5 py-2 rounded-xl border text-left text-xs font-bold shadow-2xs transition-all group/item ${
                              isPicked
                                ? 'bg-brand-50 dark:bg-brand-950/60 border-brand-500 text-brand-700 dark:text-brand-300'
                                : 'bg-white/85 dark:bg-slate-800/85 hover:bg-white dark:hover:bg-slate-800 border-slate-200/70 dark:border-slate-700/70 hover:border-brand-400 dark:hover:border-brand-500 text-slate-800 dark:text-slate-200'
                            }`}
                          >
                            <button
                              type="button"
                              onClick={() => handleProceedWithBasket(cat.id)}
                              className="flex items-center gap-2 truncate flex-1 text-left cursor-pointer hover:text-brand-600 dark:hover:text-brand-400"
                            >
                              <span className="shrink-0 scale-90">{getCategoryIcon(cat.id)}</span>
                              <span className="truncate">{cat.label}</span>
                            </button>
                            <button
                              type="button"
                              onClick={(e) => handleToggleBasketCategory(cat.id, e)}
                              title={isPicked ? 'Odebrat z výběru' : 'Přidat obor do kombinace'}
                              aria-label={`${isPicked ? 'Odebrat' : 'Přidat'} ${cat.label}`}
                              className={`px-2.5 py-1 rounded-lg text-[11px] font-extrabold border shrink-0 cursor-pointer transition-all ${
                                isPicked
                                  ? 'bg-brand-600 text-white border-brand-600'
                                  : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-600 hover:border-brand-400 hover:text-brand-600'
                              }`}
                            >
                              {isPicked ? '✓ Vybráno' : '+ Přidat'}
                            </button>
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  {/* Spodní tlačítko pro rozbalení celé oblasti */}
                  <button
                    type="button"
                    onClick={() => handleChangeGroup(sg.id)}
                    className={`w-full py-2.5 px-4 min-h-[40px] rounded-xl bg-white/95 dark:bg-slate-800/95 hover:bg-white dark:hover:bg-slate-800 border border-slate-200/90 dark:border-slate-700 text-xs font-extrabold flex items-center justify-center gap-1.5 shadow-2xs transition-all cursor-pointer ${theme.accentText}`}
                  >
                    <span>Zobrazit všech {groupCats.length} oborů v této oblasti</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              )
            })}
          </div>
        ) : selectedGroupFilter !== 'all' ? (
          /* ─── REŽIM C: ROZKLIKNUTÁ JEDNA OBLAST (s plynulým přechodem a návratem) ─── */
          <div key={selectedGroupFilter} className="space-y-6 animate-scale-in">
            {(() => {
              const currentGroup = CATEGORY_SUPER_GROUPS.find((g) => g.id === selectedGroupFilter)
              if (!currentGroup) return null
              const theme = getSuperGroupTheme(currentGroup.id)
              return (
                <div
                  className={`flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 sm:p-6 rounded-2xl bg-gradient-to-r ${theme.bannerGradient} border`}
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${theme.iconBg}`}
                    >
                      {theme.icon}
                    </div>
                    <div>
                      <span className="text-[11px] font-extrabold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                        Vybraná oblast ({filteredCategories.length} oborů)
                      </span>
                      <h3 className="font-black text-lg sm:text-xl text-slate-900 dark:text-white">
                        {currentGroup.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 mt-0.5">
                        {currentGroup.subtitle}
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleChangeGroup('overview')}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-xs font-extrabold text-slate-800 dark:text-slate-200 border border-slate-200 dark:border-slate-700 shadow-2xs cursor-pointer shrink-0 transition-all"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Zpět na 9 hlavních oblastí
                  </button>
                </div>
              )
            })()}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredCategories.map(renderCategoryCard)}
            </div>
          </div>
        ) : (
          /* ─── REŽIM D: KOMPLETNÍ KATALOG VŠECH 67 OBORŮ PODLE OBLASTÍ ─── */
          <div key="all-categories" className="space-y-10 animate-scale-in">
            {CATEGORY_SUPER_GROUPS.map((sg) => {
              const groupCats = TRADE_CATEGORIES.filter((c) => c.group === sg.id)
              const theme = getSuperGroupTheme(sg.id)
              return (
                <div key={sg.id} className="space-y-4">
                  <div
                    className={`flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-4 rounded-2xl bg-gradient-to-r ${theme.bannerGradient} border`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${theme.iconBg}`}
                      >
                        {theme.icon}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-extrabold text-base sm:text-lg text-slate-900 dark:text-white">
                            {sg.title}
                          </h3>
                          <span className="text-xs px-2.5 py-0.5 rounded-full font-bold bg-white/80 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200/60 dark:border-slate-700">
                            {groupCats.length} oborů
                          </span>
                        </div>
                        <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">
                          {sg.subtitle}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleChangeGroup(sg.id)}
                      className="text-xs font-extrabold text-brand-600 dark:text-brand-400 hover:underline cursor-pointer self-start sm:self-auto"
                    >
                      Zobrazit samostatně &rarr;
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {groupCats.map(renderCategoryCard)}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </section>

      {/* ─── 3. JAK TO FUNGUJE VE 3 KROCÍCH (S PŘECHODOVÝMI KARTAMI) ──────── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-100/90 via-white to-brand-50/50 dark:from-slate-900/90 dark:via-slate-900 dark:to-indigo-950/30 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-10 space-y-8 shadow-xs">
        <div className="text-center max-w-2xl mx-auto space-y-2">
          <span className="text-xs font-extrabold uppercase tracking-wider text-brand-600 dark:text-brand-400">
            Jednoduše a přehledně
          </span>
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white">
            Jak funguje NaceniTo.cz ve 3 krocích?
          </h2>
          <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300">
            Žádné zdlouhavé obvolávání. Zadáte své požadavky na jednom místě a zhotovitelé z okolí se ozvou vám.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800 rounded-2xl p-6 space-y-3.5 relative transition-card">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-brand-500 to-brand-700 text-white font-black text-lg flex items-center justify-center shadow-md shadow-brand-500/25">
              1
            </div>
            <h3 className="font-extrabold text-base sm:text-lg text-slate-900 dark:text-white">
              Zadáte, co potřebujete
            </h3>
            <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
              Vyberete kategorii, zaškrtnete požadované práce a můžete přiložit <strong>fotografie</strong> nebo
              si přímo v prohlížeči načrtnout <strong>vlastní nákres místnosti a rozměry</strong>.
            </p>
            <div className="pt-2 flex items-center gap-1.5 text-xs text-brand-700 dark:text-brand-300 font-bold">
              <Camera className="w-4 h-4" />
              <span>Možnost přiložit fotky i nákres</span>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800 rounded-2xl p-6 space-y-3.5 relative transition-card">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 text-white font-black text-lg flex items-center justify-center shadow-md shadow-indigo-500/25">
              2
            </div>
            <h3 className="font-extrabold text-base sm:text-lg text-slate-900 dark:text-white">
              Porovnáte cenové nabídky
            </h3>
            <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
              Ověření odborníci z vašeho okolí poptávku prozkoumají a pošlou vám odhad ceny,
              termín nástupu a své hodnocení od předchozích klientů.
            </p>
            <div className="pt-2 flex items-center gap-1.5 text-xs text-indigo-700 dark:text-indigo-300 font-bold">
              <ShieldCheck className="w-4 h-4" />
              <span>Přehledné porovnání cen a recenzí</span>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800 rounded-2xl p-6 space-y-3.5 relative transition-card">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white font-black text-lg flex items-center justify-center shadow-md shadow-emerald-500/25">
              3
            </div>
            <h3 className="font-extrabold text-base sm:text-lg text-slate-900 dark:text-white">
              Vyberete vítěznou nabídku
            </h3>
            <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
              Až podle nabídek a hodnocení si zvolíte vítěze. Teprve poté se vám oběma odemknou
              přímé telefonní kontakty pro domluvení realizace.
            </p>
            <div className="pt-2 flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-300 font-bold">
              <CheckCircle2 className="w-4 h-4" />
              <span>Plně bezpečné a nezávazné</span>
            </div>
          </div>
        </div>

        <div className="text-center pt-2">
          <button
            type="button"
            onClick={() => onNavigate('/zadat-poptavku')}
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-2xl bg-gradient-to-r from-brand-600 to-indigo-600 hover:from-brand-500 hover:to-indigo-500 text-white font-extrabold text-sm shadow-lg shadow-brand-600/20 transition-all cursor-pointer hover:-translate-y-0.5"
          >
            <PlusCircle className="w-4 h-4" />
            Vyzkoušet zadání poptávky
          </button>
        </div>
      </section>

      {/* ─── 4. VÝHODY PRO KLIENTY ────────────────────────────────────────── */}
      <section className="rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 sm:p-10 space-y-6 shadow-xs">
        <div className="text-center max-w-2xl mx-auto space-y-2">
          <span className="text-xs font-extrabold uppercase tracking-wider text-brand-600 dark:text-brand-400">
            Proč využít portál
          </span>
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white">
            Chytré nástroje pro každou zakázku
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-emerald-50/70 via-white to-white dark:from-emerald-950/20 dark:via-slate-900 dark:to-slate-900 p-5 rounded-2xl border border-slate-200/90 dark:border-slate-800 space-y-2.5 transition-card">
            <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center shadow-sm shadow-emerald-600/25">
              <Check className="w-5 h-5" />
            </div>
            <h3 className="font-extrabold text-sm text-slate-900 dark:text-white">
              Vždy 100% zdarma
            </h3>
            <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
              Zadání poptávky, příjem nabídek i výběr vítězného zhotovitele jsou pro vás zcela bezplatné.
            </p>
          </div>

          <div className="bg-gradient-to-br from-blue-50/70 via-white to-white dark:from-blue-950/20 dark:via-slate-900 dark:to-slate-900 p-5 rounded-2xl border border-slate-200/90 dark:border-slate-800 space-y-2.5 transition-card">
            <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center shadow-sm shadow-blue-600/25">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <h3 className="font-extrabold text-sm text-slate-900 dark:text-white">
              Ověřená hodnocení
            </h3>
            <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
              U každé nabídky vidíte reálná hodnocení 1–5 ★ od předchozích klientů po dokončení práce.
            </p>
          </div>

          <div className="bg-gradient-to-br from-purple-50/70 via-white to-white dark:from-purple-950/20 dark:via-slate-900 dark:to-slate-900 p-5 rounded-2xl border border-slate-200/90 dark:border-slate-800 space-y-2.5 transition-card">
            <div className="w-10 h-10 rounded-xl bg-purple-600 text-white flex items-center justify-center shadow-sm shadow-purple-600/25">
              <Layers className="w-5 h-5" />
            </div>
            <h3 className="font-extrabold text-sm text-slate-900 dark:text-white">
              Vlastní nákres v prohlížeči
            </h3>
            <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
              Přímo v zadání si můžete snadno načrtnout rozměry místnosti, linku, vanu nebo zásuvky.
            </p>
          </div>

          <div className="bg-gradient-to-br from-amber-50/70 via-white to-white dark:from-amber-950/20 dark:via-slate-900 dark:to-slate-900 p-5 rounded-2xl border border-slate-200/90 dark:border-slate-800 space-y-2.5 transition-card">
            <div className="w-10 h-10 rounded-xl bg-amber-600 text-white flex items-center justify-center shadow-sm shadow-amber-600/25">
              <Lock className="w-5 h-5" />
            </div>
            <h3 className="font-extrabold text-sm text-slate-900 dark:text-white">
              Ochrana soukromí
            </h3>
            <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
              Žádný spam ani nevyžádané hovory. Vaše telefonní číslo odemknete až vybrané nabídce.
            </p>
          </div>
        </div>
      </section>

      {/* ─── 5. ČASTÉ DOTAZY (FAQ PRO ZÁKAZNÍKY) ─── */}
      <section className="bg-white dark:bg-slate-900 rounded-3xl p-6 sm:p-8 border border-slate-200/90 dark:border-slate-800 shadow-sm space-y-5">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 dark:text-white">
            Časté dotazy k zadání poptávky a nacenění
          </h2>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
            Vše, co potřebujete vědět před zadáním poptávky na NaceniTo.cz
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-5 rounded-2xl bg-slate-50/80 dark:bg-slate-800/60 border border-slate-200/70 dark:border-slate-700/70 space-y-1.5">
            <h3 className="text-sm font-extrabold text-slate-900 dark:text-white">
              Kolik stojí zadání poptávky na NaceniTo.cz?
            </h3>
            <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
              Zadání poptávky, vlastní 2D nákres půdorysu i příjem cenových nabídek je pro zákazníky <strong>100% zdarma a nezávazné</strong>.
            </p>
          </div>

          <div className="p-5 rounded-2xl bg-slate-50/80 dark:bg-slate-800/60 border border-slate-200/70 dark:border-slate-700/70 space-y-1.5">
            <h3 className="text-sm font-extrabold text-slate-900 dark:text-white">
              Mohu v jedné poptávce kombinovat více řemesel?
            </h3>
            <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
              Ano! Můžete vybrat libovolnou kombinaci z 67 oborů najednou a sami si zvolit, zda hledáte jednu firmu na klíč, nebo samostatné řemeslníky pro každou profesi zvlášť.
            </p>
          </div>

          <div className="p-5 rounded-2xl bg-slate-50/80 dark:bg-slate-800/60 border border-slate-200/70 dark:border-slate-700/70 space-y-1.5">
            <h3 className="text-sm font-extrabold text-slate-900 dark:text-white">
              Kdy řemeslníci uvidí moje telefonní číslo a adresu?
            </h3>
            <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
              Vaše soukromí je v bezpečí. Firmy a brigádníci nejprve posílají cenové nabídky přes portál a váš telefon se odemkne teprve tehdy, když si konkrétní nabídku sami vyberete jako vítěznou.
            </p>
          </div>
        </div>
      </section>

      {/* Plovoucí spodní lišta při výběru více oborů */}
      {selectedBasketCategories.length > 0 && (
        <div className="fixed bottom-4 left-3 right-3 sm:left-1/2 sm:-translate-x-1/2 sm:max-w-3xl z-40 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-2 border-brand-500 rounded-2xl p-3.5 sm:p-4 shadow-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 animate-scale-in">
          <div className="min-w-0 flex-1">
            <div className="text-xs font-extrabold text-brand-600 dark:text-brand-400">
              Vybráno oborů do poptávky: {selectedBasketCategories.length}
            </div>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {selectedBasketCategories.map((catId) => {
                const info = getCategoryInfo(catId)
                return (
                  <span
                    key={catId}
                    className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg bg-brand-50 dark:bg-slate-800 border border-brand-200 dark:border-slate-700 text-[11px] font-bold text-slate-800 dark:text-slate-200"
                  >
                    <span className="truncate max-w-[180px]">{info.label}</span>
                    <button
                      type="button"
                      onClick={() => handleToggleBasketCategory(catId)}
                      className="text-slate-400 hover:text-rose-500 cursor-pointer"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )
              })}
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setSelectedBasketCategories([])}
              className="px-3 py-2 rounded-xl text-xs font-bold text-slate-500 hover:text-rose-600 cursor-pointer"
            >
              Zrušit
            </button>
            <button
              type="button"
              onClick={() => handleProceedWithBasket()}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-brand-600 to-indigo-600 hover:from-brand-500 hover:to-indigo-500 text-white text-xs sm:text-sm font-extrabold shadow-lg shadow-brand-600/25 cursor-pointer"
            >
              <span>Zadat poptávku ({selectedBasketCategories.length})</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default LandingPage
